import { Check, Copy, ThumbsDown, ThumbsUp } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ChatMessage } from "../../shared/types";
import { submitResponseInteraction, type FeedbackSentiment } from "../api";

type FeedbackOption = { id: string; label: string; description: string };

const feedbackOptions: Record<FeedbackSentiment, FeedbackOption[]> = {
  down: [
    { id: "wrong_career_details", label: "Wrong career details", description: "A role, skill, date, or career fact was inaccurate." },
    { id: "missed_question", label: "Missed the question", description: "The response did not address the specific enquiry." },
    { id: "outdated_information", label: "Outdated information", description: "The answer relied on information that is no longer current." },
    { id: "unprofessional_tone", label: "Unprofessional tone", description: "The language felt too casual, stiff, or off-brand." },
    { id: "logistics_error", label: "Logistics error", description: "Contact, availability, CV, or scheduling guidance was wrong." },
    { id: "other", label: "Other", description: "Share optional context below." },
  ],
  up: [
    { id: "accurate_relevant", label: "Accurate & relevant", description: "It gave the right details for the question." },
    { id: "saved_time", label: "Saved me time", description: "It quickly answered an important hiring question." },
    { id: "professional_tone", label: "Great professional tone", description: "It represented Omer clearly and professionally." },
  ],
};

type Props = {
  message: ChatMessage;
  conversation: ChatMessage[];
};

export function ResponseActions({ message, conversation }: Props) {
  const [copied, setCopied] = useState(false);
  const [sentiment, setSentiment] = useState<FeedbackSentiment | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const panelRef = useRef<HTMLElement>(null);
  const context = useMemo(() => conversation.map(({ id, role, content }) => ({ id, role, content })), [conversation]);

  useEffect(() => {
    if (!sentiment) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "nearest" }));
  }, [sentiment, selected]);

  const record = async (action: "copy" | "rating", nextSentiment?: FeedbackSentiment, categories: string[] = [], nextComment = "") => {
    if (!message.feedbackToken) return;
    await submitResponseInteraction({
      action,
      feedbackToken: message.feedbackToken,
      responseText: message.content,
      conversation: context,
      sentiment: nextSentiment,
      categories,
      comment: nextComment || undefined,
    });
  };

  const copyAnswer = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_800);
    } catch {
      setError("The answer could not be copied. Please try again.");
      return;
    }
    if (message.feedbackToken) {
      try {
        await record("copy");
      } catch {
        setError("The answer was copied, but the interaction could not be recorded.");
      }
    }
  };

  const chooseSentiment = async (next: FeedbackSentiment) => {
    if (saving) return;
    setSentiment(next);
    setSelected([]);
    setComment("");
    setSaved(false);
    setError("");
    setSaving(true);
    try {
      await record("rating", next);
    } catch {
      setError("Your rating could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (id: string) => {
    setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    setSaved(false);
  };

  const saveDetails = async () => {
    if (!sentiment || saving) return;
    setSaving(true);
    setError("");
    try {
      await record("rating", sentiment, selected, comment.trim());
      setSaved(true);
    } catch {
      setError("Your feedback could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="response-actions">
      <div className="response-action-bar" aria-label="Response actions">
        <button type="button" onClick={() => void copyAnswer()} title="Copy answer" aria-label={copied ? "Answer copied" : "Copy answer"}>
          {copied ? <Check size={15} /> : <Copy size={15} />}<span className="sr-only">{copied ? "Copied" : "Copy"}</span>
        </button>
        {message.feedbackToken && <>
          <span aria-hidden="true" />
          <button type="button" className={sentiment === "up" ? "selected positive" : ""} onClick={() => void chooseSentiment("up")} disabled={saving} title="Helpful response" aria-label="Helpful response" aria-pressed={sentiment === "up"}><ThumbsUp size={15} /></button>
          <button type="button" className={sentiment === "down" ? "selected negative" : ""} onClick={() => void chooseSentiment("down")} disabled={saving} title="Response needs improvement" aria-label="Response needs improvement" aria-pressed={sentiment === "down"}><ThumbsDown size={15} /></button>
        </>}
      </div>

      {sentiment && <section ref={panelRef} className={`feedback-panel ${sentiment}`} aria-label="Optional response feedback">
        <div className="feedback-heading">
          <strong>{sentiment === "up" ? "Thanks—what worked well?" : "Thanks—what could be better?"}</strong>
          <span>Select any that apply.</span>
        </div>
        <div className="feedback-options">
          {feedbackOptions[sentiment].map((option) => <label key={option.id}>
            <input type="checkbox" checked={selected.includes(option.id)} onChange={() => toggleCategory(option.id)} />
            <span><strong>{option.label}</strong><small>{option.description}</small></span>
          </label>)}
        </div>
        {(sentiment === "up" || selected.includes("other")) && <label className="feedback-comment">
          <span>{sentiment === "up" ? "Additional note" : "Other context"} <small>optional</small></span>
          <textarea value={comment} maxLength={600} rows={3} onChange={(event) => { setComment(event.target.value); setSaved(false); }} placeholder="Share any useful context…" />
        </label>}
        <div className="feedback-submit-row">
          <button type="button" onClick={() => void saveDetails()} disabled={saving}>{saved ? <><Check size={14} /> Saved</> : saving ? "Saving…" : "Send feedback"}</button>
          <span>Your feedback is linked to this response and conversation.</span>
        </div>
        {sentiment === "down"
          ? <p className="feedback-cta">Sorry about the mix-up. You can review Omer’s <Link to="/experience">experience</Link> or <Link to="/contact">contact him directly</Link>.</p>
          : <p className="feedback-cta">Glad that helped. Recruiters can also <Link to="/schedule">book a 30-minute introduction</Link>.</p>}
      </section>}
      {error && <p className="feedback-error" role="status">{error}</p>}
    </div>
  );
}
